import { IAllocation } from '../types';
import { ICurrencyFormat } from 'utils/types';
import { getFormattedCurrency } from 'utils/format';
import { IData } from './PieChart';
import { getTotalPercentage } from '../utils';
import orderBy from 'lodash/orderBy';
import { colors } from '@alixpartners/ui-components';

export const CHART_COLOR_PALLETTE = [
  'rgb(3,46,69)',
  'rgb(6,150,166)',
  'rgb(173,175,178)',
  'rgb(255,96,15)',
  'rgb(21,114,59)',
  'rgb(119,176,190)',
  'rgb(89,89,89)',
  'rgb(128,128,131)',
  'rgb(255,145,15)',
  'rgb(0,143,10)',
];

export const OTHERS_LABEL = 'Others';
export const OVERFLOW_PIE_PERCENT = 5;
const DEFAULT_COLOR = '#DFDFE0';

const getHatchingColor = (color?: string) => {
  return {
    pattern: {
      path: {
        d: 'M -1 1 l 2 -2 M 0 5 l 5 -5 M 4 6 l 2 -2',
        stroke: 'white',
        strokeWidth: 1,
      },
      width: 5,
      height: 5,
      backgroundColor: color || DEFAULT_COLOR,
    },
  };
};

export const OTHERS_COLOR = getHatchingColor();

export const OTHERS_CONFIG = {
  name: OTHERS_LABEL,
  value: 100,
  y: 100,
  spend: '',
  isOther: true,
  color: OTHERS_COLOR,
};

export const OVERFLOW_ELEMENT = {
  name: 'Exceeds 100%',
  value: OVERFLOW_PIE_PERCENT,
  y: OVERFLOW_PIE_PERCENT,
  spend: '',
  isOther: true,
  color: colors.red1,
};

interface IApplicationChart extends IAllocation {
  fittedPercentage?: number;
}

export const getFittedAllocations = (
  allocations: IAllocation[]
): IApplicationChart[] => {
  const data: IAllocation[] = [];
  const orderedAllocations = orderBy(
    allocations,
    ['percentage', 'name'],
    ['desc', 'asc']
  );

  for (let i = 0; i < orderedAllocations.length; i++) {
    const item = orderedAllocations[i];
    const totalPercentage = getTotalPercentage([...data, item]);
    if (totalPercentage === 100) {
      data.push(item);

      break;
    }

    if (totalPercentage > 100) {
      const pushedPercentage = getTotalPercentage(data);
      const lastItem = {
        ...item,
        fittedPercentage: 100 - pushedPercentage,
      };

      data.push(lastItem);

      break;
    }

    data.push(item);
  }

  return data;
};

export const getChartData = (
  format: ICurrencyFormat,
  spend: number,
  items?: IAllocation[] | null
) => {
  if (!items || items.length === 0) {
    return [OTHERS_CONFIG];
  }

  const totalPercentage = getTotalPercentage(items);
  let data: IData[] = [];

  if (totalPercentage <= 100) {
    data = orderBy(items, ['percentage', 'name'], ['desc', 'asc']).map(
      (item) => {
        return {
          name: item.name,
          value: item.percentage,
          y: item.percentage,
          spend: getFormattedCurrency({
            value: (spend * item.percentage) / 100,
            currencyFormat: format,
            isUnitSign: true,
            isCurrencySign: true,
            isIdealDenomination: true,
          }),
        };
      }
    );

    const othersPercentage = 100 - totalPercentage;

    if (othersPercentage > 0) {
      data.push({
        name: OTHERS_LABEL,
        value: othersPercentage,
        y: othersPercentage,
        spend: getFormattedCurrency({
          value: (spend * othersPercentage) / 100,
          currencyFormat: format,
          isUnitSign: true,
          isCurrencySign: true,
          isIdealDenomination: true,
        }),
        isOther: true,
        color: OTHERS_COLOR,
      });
    }
  } else {
    data = getFittedAllocations(items).map((item) => {
      return {
        name: item.name,
        value: item.percentage,
        y: item.fittedPercentage || item.percentage,
        spend: getFormattedCurrency({
          value: (spend * item.percentage) / 100,
          currencyFormat: format,
          isUnitSign: true,
          isCurrencySign: true,
          isIdealDenomination: true,
        }),
      };
    });

    data = [OVERFLOW_ELEMENT, ...data];
  }

  return data;
};
